import { Box, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useLazyGetPieceQuery, useUpdatePiecesMutation } from '@api/piece';
import { Piece, PieceAction } from '@apiContract';
import { usePiecePdf } from '@components/Mailpiece/hooks/usePiecePdf';
import { DialogConfirmation } from '@uiKit/DialogConfirmation';
import { SCAN } from 'src/constants';

import { useValidationExitGuard } from '../../../hooks/useValidationExitGuard';
import { ActionComponentProps } from '../../../index';
import { buildValidationPieces } from '../../../utils';
import { DialogOpenAndScan } from './components/DialogOpenAndScan';
import useNotifyComment from '../NotifyComment/useNotifyComment';
import NotifyMairoomComment from '../NotifyComment';

export const mailpieceHasContent = (mailpiece: Piece) =>
  Boolean(mailpiece.pieceDoc?.piecePages.length || mailpiece.pieceDoc?.pdfUrl);

export const OpenAndScan = ({ mailpieces, actionStarted, onActionFinish, validationData }: ActionComponentProps) => {
  const { guardedExit, exitConfirmDialog } = useValidationExitGuard(validationData, onActionFinish);

  const [updatePieces] = useUpdatePiecesMutation();

  const mailpiece = mailpieces[0];

  const [getPiece] = useLazyGetPieceQuery();
  const { handleCreatePdfFromPages } = usePiecePdf(mailpiece.id, mailpiece.pieceDoc || null);

  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();

  const [actionInProgress, setActionInProgress] = useState<boolean>(false);
  const [openAndScanOpen, setOpenAndScanOpen] = useState<boolean>(false);
  const [finishOpenAndScanOpen, setFinishOpenAndScanOpen] = useState<boolean>(false);
  const [openAndScanMode, setOpenAndScanMode] = useState<string>(SCAN);

  const { values, changeValues } = useNotifyComment();

  useEffect(() => {
    if (actionStarted) {
      if (openAndScanOpen || finishOpenAndScanOpen) return;
      const fn = !mailpieceHasContent(mailpiece) ? setOpenAndScanOpen : setFinishOpenAndScanOpen;
      fn(true);
    } else {
      setOpenAndScanOpen(false);
      setFinishOpenAndScanOpen(false);
    }
  }, [actionStarted, mailpiece, openAndScanOpen, finishOpenAndScanOpen]);

  const handleOpenAndScanComplete = (mode: string) => {
    setOpenAndScanMode(mode);
    setOpenAndScanOpen(false);
    setFinishOpenAndScanOpen(true);
  };

  const confirmFinishOpenAndScan = async () => {
    try {
      setActionInProgress(true);

      let latestPiece = mailpiece;

      if (!mailpiece.pieceDoc || !mailpiece.pieceDoc.piecePages.length) {
        const latestPieceResult = await getPiece(mailpiece.id);
        latestPiece = latestPieceResult.data || mailpiece;
      }

      if (latestPiece.pieceDoc && latestPiece.pieceDoc.piecePages.length > 0 && !latestPiece.pieceDoc.pdfUrl) {
        await handleCreatePdfFromPages(latestPiece.pieceDoc);
      }

      await updatePieces({
        pieces: buildValidationPieces([mailpiece], validationData),
        action: PieceAction.FinishOpenAndScan,
        shouldNotifyRecipient: !values.skipNotifyRecipient,
      }).unwrap();

      const comment = values.comment.trim();

      if (comment.length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom: values.notifyMailroom,
          pieceIds: [mailpiece.id],
        }).unwrap();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setActionInProgress(false);
      onActionFinish();
    }
  };

  const isScan = openAndScanMode === SCAN;

  return (
    <>
      <DialogOpenAndScan
        mailpieces={mailpieces}
        actionStarted={openAndScanOpen}
        onActionFinish={guardedExit}
        onOpenAndScanComplete={handleOpenAndScanComplete}
      />

      <DialogConfirmation
        open={finishOpenAndScanOpen}
        loading={actionInProgress}
        maxWidth="sm"
        title={isScan ? 'Convert and send item contents' : 'Send item contents'}
        content={
          <Box>
            <Typography variant="body1" color="text.secondary">
              {isScan
                ? 'Would you like to convert this item’s contents to a PDF and send it to the recipient?'
                : 'Would you like to send this item’s contents to the recipient?'}
            </Typography>

            <NotifyMairoomComment values={values} onChange={changeValues} />
          </Box>
        }
        acceptButtonHandleClick={confirmFinishOpenAndScan}
        cancelButtonHandleClick={guardedExit}
        acceptButtonTitle={isScan ? 'Convert & Send' : 'Send'}
        cancelButtonTitle={isScan ? 'No, Review Contents' : 'No, Not Now'}
      />

      {exitConfirmDialog}
    </>
  );
};
